export default {
  categories: {
    accessAccount: "Access and account",
    myActivities: "My activities",
    bookings: "Bookings",
    commissions: "Commissions",
    onSitePayments: "On-site payments (restaurants)",
    wallet: "Wallet",
    tablets: "Tablets and POS",
    support: "Support",
  },
  items: {
    accessHow: {
      question: "How do I get access to the portal?",
      answer: "After signing the contract, Flitrip support creates your account. You receive an email at the address listed in the contract with a link to set your password. If the link has expired or the email did not arrive, check spam and then open a support request.",
    },
    accessCredentials: {
      question: "How do I change my email or password?",
      answer: "Open a support ticket: credentials cannot be changed from the portal at the moment. If the staff using the portal changes, ask for a password reset immediately. Access is single and shared, so until it is changed it remains usable by anyone who had it in the past.",
    },
    accessCollaborator: {
      question: "Can I create access for a collaborator?",
      answer: "No, there is only one account. You can share it with people who work with you, keeping in mind that actions taken in the portal cannot be attributed to a specific person.",
    },
    accessClose: {
      question: "How do I end my relationship with Flitrip?",
      answer: "Open a support ticket. The termination conditions in your contract apply.",
    },
    accessApp: {
      question: "Is there an app to install?",
      answer: "No, Flitrip is a web app: you use it from the browser, including on smartphones. If you access it often from your phone, add the link to your home screen. On iPhone: Share → Add to Home Screen; on Android: browser menu → Add to Home screen. From there it opens like a normal app.",
    },
    activitiesAdd: {
      question: "How do I add a new activity?",
      answer: "Activities are created by Flitrip support. Open a ticket with name, description, duration, price, capacity and photos, and we publish it for you. Once online, you manage it yourself.",
    },
    activitiesEdit: {
      question: "What can I change on my own?",
      answer: "On a published activity you can change prices, calendar and availability, photos and description: changes go live immediately without going through support. If you change the price, confirmed bookings stay at the amount the guest paid: the change applies only to future bookings. What you cannot do on your own is create or delete an activity.",
    },
    activitiesDescription: {
      question: "What is my activity description for?",
      answer: "The description is not just for reading: it is the source the voice assistant uses to answer guest questions. If information is not written there, the assistant cannot provide it, and a guest who cannot find an answer is unlikely to book.",
    },
    activitiesLanguages: {
      question: "In which languages are my activities shown?",
      answer: "Descriptions are translated automatically: you do not need to do anything. The guest sets the tablet to their preferred language and the voice assistant speaks to them in that language.",
    },
    activitiesAvailability: {
      question: "How do I manage calendar and availability?",
      answer: "From Activities → [activity name] → Availability, define bookable days and time slots, maximum participants and closed dates for holidays or closures. On closed days the activity stays visible but not bookable, and you reopen when you want. For very long closures, open a support ticket.",
    },
    activitiesDelete: {
      question: "How do I delete an activity?",
      answer: "Deletion must be requested from support with a ticket. If you only need to stop bookings for a period, close dates in Availability: it is immediate and reversible.",
    },
    activitiesPhotos: {
      question: "What are the photo requirements?",
      answer: "JPG and PNG are accepted. There is no weight limit, but to avoid slowing uploads on tablets stay under 2 MB per image, with the long side around 2000 pixels. The first photo is the cover: it is what the guest sees when browsing offers, so choose the most recognizable image and use a horizontal format. Avoid logos, watermarks, overlaid text and collages. If you upload from iPhone, photos are saved in HEIC, which is not accepted. Go to Settings → Camera → Formats → Most Compatible, or convert photos before uploading.",
    },
    bookingsHandshake: {
      question: "What is handshake and should I enable it?",
      answer: "Handshake decides what happens when a booking arrives. You enable or disable it in Settings → Profile, and it applies to all your activities. Handshake on: every booking arrives as a request; you can confirm, reject or reschedule it, and you can chat with the guest. Use it if you have limited seats, want to filter requests or need to agree on times first. Handshake off: every booking is confirmed automatically. You do not need to monitor anything, but you cannot reschedule or contact the guest: for any need you must go through support. If you are unsure, without handshake the only way to get out of a booking you cannot honour is to cancel it.",
    },
    bookingsNotifications: {
      question: "How do I receive booking notifications?",
      answer: "By email or WhatsApp, your choice: set the channel in Settings → Profile. You can always find them all in Bookings, which remains the reference if a notification is missed.",
    },
    bookingsNoConfirm: {
      question: "What happens if I do not confirm a booking?",
      answer: "Only with handshake enabled. After 15 minutes with no response you receive an automatic call that reads the booking details: press 1 on the keypad and the booking is confirmed instantly without opening the portal. The call comes from an international +1 number: it is not spam. Save it in your contacts. If you do not respond to the notification or the call, after 24 hours of inactivity the request expires and the guest is refunded.",
    },
    bookingsLeadTime: {
      question: "How much advance notice is required to book?",
      answer: "You set it in your profile configuration: define how many hours before a guest can still book. Calibrate this to the time you actually need to prepare. Guests book from the in-room tablet, often the same evening for the same evening: too much notice makes you miss the most frequent bookings.",
    },
    bookingsCancel: {
      question: "Can I cancel a confirmed booking?",
      answer: "Yes, by opening the booking at any time. The guest is refunded immediately. Before cancelling, consider alternatives. With handshake on you can reschedule or agree in chat. Without handshake, open a ticket: support can contact the guest for you and suggest another date. A rescheduled guest is much better than a refunded one.",
    },
    bookingsReschedule: {
      question: "Can I reschedule a booking or change the number of guests?",
      answer: "Only with handshake enabled, by opening the booking. Without handshake you must go through support, or cancel.",
    },
    bookingsGuestId: {
      question: "How do I identify the guest when they arrive?",
      answer: "By first and last name, which you find in Bookings. When confirmed, the guest receives a PDF with all booking details via WhatsApp or email, their choice: if in doubt, ask them to show it — they have it on their phone.",
    },
    bookingsContactGuest: {
      question: "How do I contact a guest before the experience?",
      answer: "With handshake enabled, from the chat inside the booking. Without handshake, contact support by opening a ticket: the guest's direct contact details are not provided to you.",
    },
    bookingsOverbooking: {
      question: "Could I receive more bookings than I can handle?",
      answer: "No. The system is based on the availability you set: once the number of seats you indicated for a time slot is reached, that slot is no longer bookable. Keep in mind these numbers only cover the Flitrip channel: they do not account for phone bookings or walk-ins. Set availability considering how much capacity you really want to reserve for Flitrip.",
    },
    commissions: {
      question: "What are the commissions?",
      answer: "The commission applied to you is the one set in the contract you signed. If you have doubts about how a commission was calculated, contact support.",
    },
    paymentsOnSite: {
      question: "How do I get paid?",
      answer: "The guest pays you directly on site at the end of the service. Flitrip does not collect anything on your behalf and there is no balance to wait for.",
    },
    paymentsReceiptWhy: {
      question: "Why do I need to upload the receipt?",
      answer: "The receipt lets us calculate the commission due under the contract. Without an uploaded receipt, the booking is not considered completed. Upload the photo by opening the booking at the end of the service: make it a habit before closing the day so none are left behind.",
    },
    paymentsReceiptHow: {
      question: "What exactly should I photograph?",
      answer: "The receipt or invoice issued to the guest for that booking, with readable amount and date. A well-taken phone photo is enough.",
    },
    walletPayouts: {
      question: "When do I receive payments?",
      answer: "Payments are made according to the times and terms in your contract: you do not need to request a withdrawal. In Wallet you can see your balance and transaction history at any time.",
    },
    walletReports: {
      question: "How do I get reports and summaries for my accountant?",
      answer: "Request them from support by opening a ticket, indicating the period you need. In Wallet you can still check balance and transactions at any time.",
    },
    tabletsHow: {
      question: "How does Flitrip work in my property?",
      answer: "Flitrip installs an in-room tablet with a paired POS through which your guests discover and book local experiences, services and restaurants, guided by a voice assistant that speaks their language. For every booking made from your property's tablets you receive a commission as set out in the contract. You do not sell or manage anything: your job is to keep the devices ready to use.",
    },
    tabletsOwnership: {
      question: "Do I own the devices?",
      answer: "No. Tablets and POS are provided by Flitrip on loan: they remain Flitrip property and we collect them when the relationship ends.",
    },
    tabletsDuty: {
      question: "What do I need to take care of?",
      answer: "One thing, but important: keep devices charged and ready to use, placed conveniently and clearly visible to the guest. A dead tablet or one tucked in a drawer does not generate bookings, and therefore no commission for you.",
    },
    tabletsBroken: {
      question: "A guest says the tablet is not working",
      answer: "Distinguish two cases. If payment did not go through, the guest can simply try again on the tablet: no need to report anything. If the device is faulty or will not turn on, open a ticket indicating the room. We handle replacement at no cost to you.",
    },
    tabletsDamage: {
      question: "What if a guest damages or takes a tablet?",
      answer: "Flitrip takes responsibility. Nothing is charged to you: we only ask you to report it with a ticket so we can restore the device.",
    },
    tabletsEarnings: {
      question: "How do I see how much I am earning?",
      answer: "In Bookings you find the full history of bookings from your tablets and in Wallet the accrued balance with all movements. Guest data you see is confidential and may only be used for purposes related to the service.",
    },
    tabletsGuestIssue: {
      question: "A guest has a problem with a booking, what do I do?",
      answer: "Ask the guest to follow the instructions to contact Flitrip support, found on the tablet and in the booking confirmation they received. Handling is entirely ours: you do not need to resolve it yourself.",
    },
    tabletsOwnActivities: {
      question: "Can I also publish my own activities?",
      answer: "Yes. In that case a partner profile is added alongside your host profile and in the portal you also find activity and booking management. Open a support ticket to enable it.",
    },
    tabletsReturn: {
      question: "What happens to the devices if I end the relationship?",
      answer: "We collect the tablets and POS. You do not need to ship anything or organise the return.",
    },
    supportTicket: {
      question: "How do I open a ticket?",
      answer: "From Help & Support → Submit ticket. Track your requests from My tickets.",
    },
  },
} as const;
